import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, TrophyIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';

interface PrizeBreakdownEntry {
  id: string;
  position_label: string;
  category: string;
  prize_amount: number;
  currency: string;
  description?: string;
  display_order: number;
}

interface PrizeBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournamentId: string;
  tournamentName: string;
}

export default function PrizeBreakdownModal({
  isOpen,
  onClose,
  tournamentId,
  tournamentName
}: PrizeBreakdownModalProps) {
  const [prizes, setPrizes] = useState<PrizeBreakdownEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPrizePool, setTotalPrizePool] = useState(0);

  useEffect(() => {
    if (isOpen && tournamentId) {
      fetchPrizeBreakdown();
    }
  }, [isOpen, tournamentId]);

  const fetchPrizeBreakdown = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('prize_breakdown')
        .select('*')
        .eq('tournament_id', tournamentId)
        .order('display_order', { ascending: true });

      if (error) throw error;

      if (data) {
        setPrizes(data);

        // Calculate total prize pool
        const total = data.reduce((sum, prize) => sum + Number(prize.prize_amount), 0);
        setTotalPrizePool(total);
      }
    } catch (err) {
      console.error('Error fetching prize breakdown:', err);
      setPrizes([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = '₦') => {
    return `${currency}${amount.toLocaleString()}`;
  };

  // Group prizes by category
  const prizesByCategory = prizes.reduce((acc, prize) => {
    if (!acc[prize.category]) {
      acc[prize.category] = [];
    }
    acc[prize.category].push(prize);
    return acc;
  }, {} as Record<string, PrizeBreakdownEntry[]>);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="bg-gradient-to-r from-golf-green to-golf-orange p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="bg-white/20 p-2 rounded-lg">
                        <TrophyIcon className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <Dialog.Title as="h3" className="text-2xl font-bold text-white">
                          Prize Breakdown
                        </Dialog.Title>
                        <p className="text-white/90 text-sm mt-1">{tournamentName}</p>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="text-white hover:text-white/80 transition-colors"
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Total Prize Pool */}
                  {totalPrizePool > 0 && (
                    <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <CurrencyDollarIcon className="w-5 h-5 text-white" />
                          <span className="text-white/90 text-sm font-medium">Total Prize Pool</span>
                        </div>
                        <span className="text-2xl font-bold text-white">
                          {formatCurrency(totalPrizePool, prizes[0]?.currency)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6 max-h-[600px] overflow-y-auto">
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-golf-orange"></div>
                    </div>
                  ) : prizes.length === 0 ? (
                    <div className="text-center py-12">
                      <TrophyIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No prize breakdown available yet</p>
                      <p className="text-gray-400 text-sm mt-2">
                        Prize details will be announced soon
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {Object.entries(prizesByCategory).map(([category, categoryPrizes]) => (
                        <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                            <h4 className="text-lg font-semibold text-gray-900">{category}</h4>
                          </div>

                          <div className="divide-y divide-gray-200">
                            {categoryPrizes.map((prize, index) => (
                              <div
                                key={prize.id}
                                className={`p-4 hover:bg-gray-50 transition-colors ${
                                  index === 0 ? 'bg-yellow-50' : ''
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-4">
                                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold ${
                                      index === 0 ? 'bg-yellow-100 text-yellow-800' :
                                      index === 1 ? 'bg-gray-100 text-gray-800' :
                                      index === 2 ? 'bg-orange-100 text-orange-800' :
                                      'bg-blue-50 text-blue-700'
                                    }`}>
                                      {index === 0 ? (
                                        <TrophyIcon className="w-6 h-6" />
                                      ) : (
                                        <span className="text-sm">{index + 1}</span>
                                      )}
                                    </div>
                                    <div>
                                      <h5 className="text-base font-semibold text-gray-900">
                                        {prize.position_label}
                                      </h5>
                                      {prize.description && (
                                        <p className="text-sm text-gray-600 mt-1">
                                          {prize.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-2xl font-bold text-golf-orange">
                                      {formatCurrency(prize.prize_amount, prize.currency)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}

                      {/* Glossary Footer */}
                      <div className="bg-gray-50 rounded-lg p-4 mt-6">
                        <h5 className="text-sm font-semibold text-gray-700 mb-2">Prize Notes</h5>
                        <ul className="text-xs text-gray-600 space-y-1">
                          <li>• Prizes are subject to applicable taxes and regulations</li>
                          <li>• Winners must be present to claim their prizes</li>
                          <li>• All decisions by the tournament committee are final</li>
                          <li>• Prize amounts are in Nigerian Naira (₦) unless otherwise specified</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
                  <button
                    onClick={onClose}
                    className="px-6 py-2 bg-golf-orange text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
